import { z } from 'zod';
/**
 * A loja online — F221, ADR-0041.
 *
 * Duas metades:
 *
 * * **O que o site lê** (`/storefront/:slug/...`), com nomes em português
 *   porque é o vocabulário que o site de atacado já usa (`codigoProduto`,
 *   `nome`, `preco`). Dinheiro em texto decimal, como em todo o Aeris — quem
 *   converte para número de tela é o site.
 * * **O que a loja configura** (`/storefront-admin/...`), no padrão do resto
 *   da API.
 *
 * O lojista entra só com o telefone (decisão do dono, 04/10), e por isso o que
 * volta dele é o mínimo: nome e código. Documento, endereço, saldo, vale e
 * crediário não têm campo aqui onde caberiam.
 */
const SLUG = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
/** Reais em texto, até seis casas — "19.90" (F247). Vírgula não: o site manda ponto. */
const StorefrontPriceSchema = z
    .string()
    .trim()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, 'O preço deve ser um número, como 19.90');
export const StorefrontSlugParams = z.object({
    slug: z.string().regex(SLUG),
});
export const StorefrontLoginRequest = z.object({
    /** O telefone como a pessoa digitou; o servidor tira o que não é dígito. */
    numero: z.string().trim().min(1).max(40),
});
export const StorefrontShopkeeper = z.object({
    id: z.string(),
    nome: z.string(),
    codigo: z.string().nullable(),
    numero: z.string(),
});
export const StorefrontLoginResponse = z.object({
    token: z.string(),
    usuario: StorefrontShopkeeper,
});
export const StorefrontMeResponse = z.object({
    usuario: StorefrontShopkeeper.omit({ numero: true }),
    loja: z.object({ nome: z.string() }),
});
export const StorefrontProductsQuery = z.object({
    termo: z.string().trim().max(120).optional(),
    /** O id de uma categoria de `GET /:slug/categories`. */
    categoria: z.uuid().optional(),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    limit: z.coerce.number().int().min(1).max(60).default(24),
    /**
     * A ordem (F247: o preço). Com estoque continua vindo primeiro; a ordem vale
     * dentro de cada metade, como o site sempre mostrou.
     */
    ordenar: z.enum(['az', 'za', 'preco_asc', 'preco_desc']).default('az'),
    /**
     * A faixa de preço, em reais (F247), na tabela padrão da loja — a mesma do
     * preço que a loja mostra. Texto decimal, nunca float: "19.90".
     */
    precoMin: StorefrontPriceSchema.optional(),
    precoMax: StorefrontPriceSchema.optional(),
    /**
     * `menu`: a vitrine geral ("Todos", "Recomendados"), sem os produtos das
     * categorias que a loja tirou do menu (F270) — a peça fica na Pesquisa por
     * Peça. Só vale sem `categoria`.
     */
    vitrine: z.enum(['menu']).optional(),
});
export const StorefrontProduct = z.object({
    id: z.string(),
    /** O código da cor padrão — o que o vendedor procura no balcão. */
    codigoProduto: z.string(),
    nome: z.string(),
    descricao: z.string().nullable(),
    /** A categoria de cima; com subcategoria, a filha vem em `subcategoria` (F243). */
    categoria: z.string().nullable(),
    subcategoria: z.string().nullable().default(null),
    /** Preço da tabela padrão, em texto decimal ("12.50"); nulo sem preço. */
    preco: z.string().nullable(),
    /**
     * Tem para vender na filial da loja: o disponível somado das cores ativas.
     * Sem estoque o produto não some — vem no fim, para o "avise-me".
     */
    emEstoque: z.boolean(),
    imagemUrl: z.string().nullable(),
});
export const StorefrontProductsResponse = z.object({
    data: z.array(StorefrontProduct),
    pagination: z.object({
        page: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
export const StorefrontProductResponse = z.object({
    data: StorefrontProduct,
});
const StorefrontCategory = z.object({
    id: z.string(),
    nome: z.string(),
    produtos: z.number().int(),
});
/**
 * A árvore do menu (F243): as categorias de cima, cada uma com as filhas.
 * A contagem da de cima inclui as filhas, e `?categoria=<id da de cima>` as
 * traz também. Categoria sem produto ativo não vem.
 */
/**
 * Os ícones do menu (F270): nomes que o site conhece e desenha (lucide). Lista
 * fechada de propósito — um nome que o site não conhece viraria um quadrado.
 */
export const STOREFRONT_MENU_ICONS = [
    'smartphone',
    'cable',
    'plug',
    'battery',
    'monitor',
    'mouse',
    'keyboard',
    'printer',
    'headphones',
    'mic',
    'speaker',
    'radio',
    'car',
    'home',
    'lamp',
    'wrench',
    'flashlight',
    'lock',
    'gamepad',
    'tv',
    'camera',
    'router',
    'book',
    'pencil',
    'backpack',
    'sparkles',
    'blocks',
    'watch',
    'phone',
    'cup',
    'gift',
    'shirt',
    'umbrella',
    'zap',
    'package',
];
/**
 * O menu (F270): `menu: false` é a categoria que a loja tirou do menu (as de
 * peça) — continua aqui porque a Pesquisa por Peça acha o id pelo nome. A
 * ordem da lista é a ordem do menu.
 */
const StorefrontMenuFields = z.object({
    menu: z.boolean().default(true),
    /** Fora do menu, vira aba na Pesquisa por Peça (F292). */
    aba: z.boolean().default(true),
    icone: z.string().nullable().default(null),
});
export const StorefrontCategoriesResponse = z.object({
    data: z.array(StorefrontCategory.extend(StorefrontMenuFields.shape).extend({
        subcategorias: z
            .array(StorefrontCategory.extend(StorefrontMenuFields.shape))
            .default([]),
    })),
});
/* ------------------------------------------------------------------ */
/* A configuração, na tela do Aeris                                    */
/* ------------------------------------------------------------------ */
const ORIGIN = z
    .string()
    .trim()
    .regex(/^https?:\/\/[a-z0-9.-]+(:\d{1,5})?$/i, 'Origem inválida.');
export const StorefrontSummary = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    branchId: z.string(),
    allowedOrigins: z.array(z.string()),
    status: z.enum(['active', 'archived']),
    version: z.number().int(),
});
export const StorefrontListResponse = z.object({
    storefronts: z.array(StorefrontSummary),
});
export const StorefrontResponse = z.object({
    storefront: StorefrontSummary,
});
export const CreateStorefrontRequest = z.object({
    slug: z
        .string()
        .trim()
        .toLowerCase()
        .regex(SLUG, 'Use letras, números e hífen.'),
    name: z.string().trim().min(1).max(80),
    branchId: z.uuid(),
    allowedOrigins: z.array(ORIGIN).max(10).default([]),
});
export const UpdateStorefrontRequest = z.object({
    version: z.number().int().positive(),
    name: z.string().trim().min(1).max(80),
    branchId: z.uuid(),
    allowedOrigins: z.array(ORIGIN).max(10),
    status: z.enum(['active', 'archived']),
});
export const StorefrontSessionsResponse = z.object({
    sessions: z.array(z.object({
        partyId: z.string(),
        name: z.string(),
        code: z.string().nullable(),
        sessions: z.number().int(),
        lastSeenAt: z.string(),
    })),
});
export const RevokeStorefrontSessionsResponse = z.object({
    revoked: z.number().int(),
});
// ---------------------------------------------------------------------------
// O pedido da loja — F224
// ---------------------------------------------------------------------------
/** As vendedoras da loja, com a que já atende este cliente marcada. */
export const StorefrontSellersResponse = z.object({
    vendedores: z.array(z.object({
        id: z.string(),
        nome: z.string(),
        /** Só dígitos, com o 55, como o `wa.me` pede. */
        telefones: z.array(z.string()),
    })),
    /** A vendedora que vem marcada; nula é ninguém marcado. */
    sugerido: z.string().nullable(),
});
/**
 * O carrinho que vira pedido.
 *
 * Só o código e a quantidade: o nome e o preço saem do servidor, da tabela da
 * loja — o site não diz quanto custa. `chave` é a do carrinho: o mesmo clique
 * mandado duas vezes é o mesmo pedido.
 */
export const StorefrontPlaceOrderRequest = z.object({
    chave: z.uuid(),
    vendedorId: z.uuid(),
    itens: z
        .array(z.object({
        codigo: z.string().trim().min(1).max(60),
        quantidade: z.number().int().min(1).max(9999),
    }))
        .min(1)
        .max(100),
    observacao: z.string().trim().max(500).optional(),
});
/**
 * Onde o pedido está, nas palavras do cliente. Vem do pedido da loja e do
 * pedido de venda que a aprovação criou (a saída com o motoboy, a entrega).
 */
export const StorefrontOrderSituation = z.enum([
    'aguardando',
    'aprovado',
    'saiu_para_entrega',
    'entregue',
    'recusado',
    'cancelado',
]);
export const StorefrontOrder = z.object({
    id: z.string(),
    numero: z.number().int(),
    situacao: StorefrontOrderSituation,
    /** Aprovado com menos do que o pedido (uma linha a menos, ou menos peças). */
    parcial: z.boolean(),
    vendedor: z.object({ nome: z.string() }).nullable(),
    total: z.string(),
    totalAprovado: z.string().nullable(),
    motivoRecusa: z.string().nullable(),
    observacao: z.string().nullable(),
    criadoEm: z.string(),
    decididoEm: z.string().nullable(),
    itens: z.array(z.object({
        codigo: z.string(),
        nome: z.string(),
        unidade: z.string(),
        quantidade: z.string(),
        quantidadeAprovada: z.string().nullable(),
        preco: z.string(),
    })),
});
/** O pedido gravado, e o WhatsApp da vendedora com o texto pronto. */
export const StorefrontPlaceOrderResponse = z.object({
    pedido: StorefrontOrder,
    whatsapp: z.string(),
    texto: z.string(),
});
export const StorefrontOrdersResponse = z.object({
    data: z.array(StorefrontOrder),
});
export const StorefrontOrderResponse = z.object({ data: StorefrontOrder });
/**
 * Minhas compras (F290): tudo o que o cliente levou no mês — balcão, pedido e
 * site —, com as peças e como pagou. Sem saldo: o Eduardo pediu para não
 * mostrar, porque confunde.
 */
export const StorefrontPurchasesQuery = z.object({
    mes: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mês no formato AAAA-MM.')
        .optional(),
});
const FormaPaga = z.object({
    forma: z.string(),
    nome: z.string(),
    valor: z.string(),
});
export const StorefrontPurchasesResponse = z.object({
    mes: z.string(),
    total: z.string(),
    porForma: z.array(FormaPaga),
    aPrazo: z.string(),
    compras: z.array(z.object({
        numero: z.number().int().nullable(),
        tipo: z.enum(['balcao', 'pedido']),
        data: z.string(),
        total: z.string(),
        itens: z.array(z.object({
            codigo: z.string(),
            descricao: z.string(),
            quantidade: z.string(),
            preco: z.string(),
            total: z.string(),
        })),
        pagamento: z.array(FormaPaga),
        aPrazo: z.string(),
    })),
});
export const StorefrontOrderParams = StorefrontSlugParams.extend({
    numero: z.coerce.number().int().min(1).max(10_000_000),
});
/** A fila da vendedora no Aeris: `/storefront-orders` (F224). */
export const StoreOrderStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'cancelled',
]);
export const StoreOrderSummary = z.object({
    id: z.string(),
    number: z.number().int(),
    status: StoreOrderStatusSchema,
    customerName: z.string(),
    customerCode: z.string().nullable(),
    sellerUserId: z.string().nullable(),
    sellerName: z.string().nullable(),
    totalAmount: z.string(),
    approvedAmount: z.string().nullable(),
    rejectReason: z.string().nullable(),
    note: z.string().nullable(),
    salesDocumentId: z.string().nullable(),
    /** O número do pedido de venda criado — o que Vendas mostra (F248). */
    salesDocumentNumber: z.number().int().nullable().default(null),
    /** O telefone do cliente, para a vendedora chamar quando o aviso não sai (F248). */
    customerPhone: z.string().nullable().default(null),
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    customerNotifiedAt: z.string().nullable(),
    notifySkipReason: z.string().nullable(),
    version: z.number().int(),
    lines: z.array(z.object({
        id: z.string(),
        lineNumber: z.number().int(),
        code: z.string(),
        description: z.string(),
        unit: z.string(),
        quantity: z.string(),
        approvedQuantity: z.string().nullable(),
        /** O preço que o cliente viu. */
        unitPrice: z.string(),
        /** O disponível agora na filial da loja; nulo é "nunca se moveu". */
        available: z.string().nullable(),
        /** A foto principal da peça, para a vendedora pegar a certa (F286). */
        thumbUrl: z.string().nullable().default(null),
    })),
});
export const StoreOrderListQuery = z.object({
    state: z.enum(['pending', 'decided']).default('pending'),
    /** Só os da vendedora logada. */
    mine: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
});
export const StoreOrderListResponse = z.object({
    orders: z.array(StoreOrderSummary),
});
export const StoreOrderResponse = z.object({ order: StoreOrderSummary });
export const StoreOrderPendingCountResponse = z.object({
    pending: z.number().int().nonnegative(),
    mine: z.number().int().nonnegative(),
});
const QUANTITY = /^\d{1,12}(\.\d{1,6})?$/;
/**
 * Aprovar, linha a linha: a quantidade que vai (zero tira a linha; menos que
 * o pedido é o aceite parcial). Com a versão que a vendedora leu.
 */
export const ApproveStoreOrderRequest = z.object({
    version: z.number().int().positive(),
    lines: z
        .array(z.object({
        lineId: z.uuid(),
        quantity: z.string().trim().regex(QUANTITY, 'Quantidade inválida'),
    }))
        .min(1)
        .max(100),
});
export const RejectStoreOrderRequest = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3).max(300),
});
// ---------------------------------------------------------------------------
// As métricas da loja — F226
// ---------------------------------------------------------------------------
/**
 * Um lote do que aconteceu no site. Vai como TEXTO (`text/plain`, com o JSON
 * dentro) e com o token NO CORPO: assim o navegador manda direto, sem a
 * pergunta de permissão (preflight) do CORS, e o `sendBeacon` da saída da
 * página também consegue mandar. O cliente vem do token, nunca do corpo.
 */
export const StorefrontEventsBody = z.object({
    token: z.string().max(200).optional(),
    /** A sessão do navegador (o `_sid` do site): conta visitantes. */
    sessao: z.string().trim().min(8).max(64),
    eventos: z
        .array(z.object({
        tipo: z.enum([
            'visit',
            'page_view',
            'category',
            'product_view',
            'add_to_cart',
            'checkout_start',
        ]),
        caminho: z.string().max(200).optional(),
        termo: z.string().trim().max(120).optional(),
        codigo: z.string().trim().max(60).optional(),
        quantidade: z.number().int().min(1).max(9999).optional(),
    }))
        .min(1)
        .max(25),
});
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** O período da aba Métricas, em dias de São Paulo (até 92). */
export const StorefrontMetricsQuery = z.object({
    from: z.string().regex(DAY),
    to: z.string().regex(DAY),
});
const ProductCountSchema = z.object({
    code: z.string(),
    name: z.string().nullable(),
    count: z.number().int(),
    quantity: z.number().int(),
});
export const StorefrontMetricsResponse = z.object({
    from: z.string(),
    to: z.string(),
    visitors: z.number().int(),
    pageViews: z.number().int(),
    logins: z.number().int(),
    customersIn: z.number().int(),
    refusedLogins: z.array(z.object({
        phone: z.string(),
        attempts: z.number().int(),
        lastAt: z.string(),
    })),
    searches: z.number().int(),
    topTerms: z.array(z.object({
        term: z.string(),
        count: z.number().int(),
        results: z.number().int(),
    })),
    zeroResultTerms: z.array(z.object({ term: z.string(), count: z.number().int(), lastAt: z.string() })),
    topViewed: z.array(ProductCountSchema),
    topAdded: z.array(ProductCountSchema),
    checkoutStarts: z.number().int(),
    orders: z.object({
        placed: z.number().int(),
        approved: z.number().int(),
        rejected: z.number().int(),
        cancelled: z.number().int(),
        pending: z.number().int(),
        placedAmount: z.string(),
        approvedAmount: z.string(),
    }),
    bySeller: z.array(z.object({
        sellerName: z.string().nullable(),
        placed: z.number().int(),
        approved: z.number().int(),
        approvedAmount: z.string(),
    })),
    customers: z.array(z.object({
        partyId: z.string(),
        name: z.string(),
        code: z.string().nullable(),
        logins: z.number().int(),
        lastSeenAt: z.string().nullable(),
        searches: z.number().int(),
        addedToCart: z.number().int(),
        orders: z.number().int(),
        ordersAmount: z.string(),
    })),
});
/* ------------------------------------------------------------------ */
/* O banner da loja (F269)                                             */
/* ------------------------------------------------------------------ */
/** O que o site lê: só o que está no ar agora, na ordem do carrossel. */
export const StorefrontPublicBannersResponse = z.object({
    data: z.array(z.object({
        id: z.string(),
        titulo: z.string(),
        imagemDesktop: z.string(),
        /** Nula: o site usa a do computador também no celular. */
        imagemCelular: z.string().nullable(),
        link: z.string().nullable(),
    })),
});
const BannerImageSchema = z.object({
    url: z.string(),
    width: z.number().int(),
    height: z.number().int(),
    byteSize: z.number().int(),
});
export const StorefrontBannerSchema = z.object({
    id: z.string(),
    title: z.string(),
    linkUrl: z.string().nullable(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    active: z.boolean(),
    position: z.number().int(),
    desktop: BannerImageSchema.nullable(),
    mobile: BannerImageSchema.nullable(),
    /** No ar agora: ligado, dentro do período e com a imagem do computador. */
    live: z.boolean(),
    version: z.number().int(),
});
export const StorefrontBannerListResponse = z.object({
    banners: z.array(StorefrontBannerSchema),
});
export const StorefrontBannerResponse = z.object({
    banner: StorefrontBannerSchema,
});
const BANNER_LINK = z
    .string()
    .trim()
    .max(500)
    .regex(/^(\/[^/]|https:\/\/)/, 'Use um caminho da loja (/shop/…) ou um endereço https://.');
const BannerFieldsSchema = z.object({
    title: z.string().trim().min(1).max(120),
    linkUrl: BANNER_LINK.nullable().default(null),
    startsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    endsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    active: z.boolean().default(true),
});
const periodInOrder = (value) => value.startsAt === null ||
    value.endsAt === null ||
    new Date(value.startsAt) < new Date(value.endsAt);
const PERIOD_MESSAGE = {
    message: 'O fim tem de ser depois do início.',
    path: ['endsAt'],
};
export const CreateStorefrontBannerRequest = BannerFieldsSchema.refine(periodInOrder, PERIOD_MESSAGE);
export const UpdateStorefrontBannerRequest = BannerFieldsSchema.extend({
    version: z.number().int().positive(),
}).refine(periodInOrder, PERIOD_MESSAGE);
export const ReorderStorefrontBannersRequest = z.object({
    ids: z.array(z.uuid()).max(50),
});
/* ------------------------------------------------------------------ */
/* O menu de categorias (F270)                                         */
/* ------------------------------------------------------------------ */
const MenuLeafSchema = z.object({
    id: z.string(),
    name: z.string(),
    products: z.number().int(),
    inMenu: z.boolean(),
    inTabs: z.boolean().default(true),
    position: z.number().int().nullable(),
    icon: z.string().nullable(),
});
export const StorefrontMenuResponse = z.object({
    categories: z.array(MenuLeafSchema.extend({ children: z.array(MenuLeafSchema) })),
});
export const SaveStorefrontMenuRequest = z.object({
    items: z
        .array(z.object({
        categoryId: z.uuid(),
        inMenu: z.boolean(),
        // Ausente é "continua aba": a tela de antes não mandava (F292).
        inTabs: z.boolean().default(true),
        position: z.number().int().min(0).max(10_000).nullable(),
        icon: z.enum(STOREFRONT_MENU_ICONS).nullable(),
    }))
        .max(2000),
});
//# sourceMappingURL=storefront.js.map